import React, { useState } from 'react';
import { callAI } from '../services/aiApi';
import { showAlert } from '../utils/swal';

export default function AIAssistantDrawer() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('jobseeker');
  const [selectedFeature, setSelectedFeature] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Form Inputs State
  const [inputs, setInputs] = useState({
    jobTitle: '',
    companyName: '',
    skills: '',
    location: '',
    experienceLevel: '',
    resumeText: '',
    jobDescription: '',
    currentRole: '',
    targetRole: '',
    userQuery: '',
    candidateName: '',
    emailType: 'Offer',
  });

  // Auto populate from logged-in user profile if available
  React.useEffect(() => {
    if (isOpen) {
      const userStr = localStorage.getItem('user');
      if (userStr && userStr !== 'undefined') {
        try {
          const user = JSON.parse(userStr);
          setInputs(prev => ({
            ...prev,
            companyName: user.company || prev.companyName,
            candidateName: user.name || prev.candidateName,
            skills: user.skills ? (Array.isArray(user.skills) ? user.skills.join(', ') : user.skills) : prev.skills,
            resumeText: user.summary || prev.resumeText,
            currentRole: user.title || prev.currentRole,
          }));
        } catch (e) {
          console.error(e);
        }
      }
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const handleRunAI = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);

    try {
      let res;
      switch (selectedFeature) {
        // Job Seeker Features
        case 'parseResume':
          res = await callAI('parseResume', { resumeText: inputs.resumeText });
          break;
        case 'optimizeProfile':
          res = await callAI('optimizeProfile', { profileText: inputs.resumeText });
          break;
        case 'generateCoverLetter':
          res = await callAI('generateCoverLetter', {
            resumeText: inputs.resumeText,
            jobDescription: inputs.jobDescription,
          });
          break;
        case 'checkATS':
          res = await callAI('checkATS', {
            resumeText: inputs.resumeText,
            jobDescription: inputs.jobDescription,
          });
          break;
        case 'analyzeSkillGap':
          res = await callAI('analyzeSkillGap', {
            userSkills: inputs.skills,
            targetRole: inputs.targetRole,
          });
          break;
        case 'generateInterviewPrep':
          res = await callAI('generateInterviewPrep', { targetRole: inputs.targetRole });
          break;
        case 'generateCareerRoadmap':
          res = await callAI('generateCareerRoadmap', {
            currentRole: inputs.currentRole,
            targetRole: inputs.targetRole,
          });
          break;
        case 'getCareerCoachAdvice':
          res = await callAI('getCareerCoachAdvice', {
            userQuery: inputs.userQuery,
            profileContext: inputs.resumeText || inputs.currentRole,
          });
          break;
        case 'calculateJobMatch':
          res = await callAI('calculateJobMatch', {
            userProfile: { skills: inputs.skills, role: inputs.currentRole, summary: inputs.resumeText },
            jobDescription: inputs.jobDescription,
          });
          break;

        // Employer Features
        case 'generateJobDescription':
          res = await callAI('generateJobDescription', {
            jobTitle: inputs.jobTitle,
            skills: inputs.skills,
            companyName: inputs.companyName,
          });
          break;
        case 'recommendSalary':
          res = await callAI('recommendSalary', {
            jobTitle: inputs.jobTitle,
            location: inputs.location,
            experienceLevel: inputs.experienceLevel,
          });
          break;
        case 'generateScreeningQuestions':
          res = await callAI('generateScreeningQuestions', {
            jobDescription: inputs.jobDescription,
          });
          break;
        case 'generateEmail':
          res = await callAI('generateEmail', {
            type: inputs.emailType,
            candidateName: inputs.candidateName,
            companyName: inputs.companyName,
            position: inputs.jobTitle,
          });
          break;
        case 'detectFraud':
          res = await callAI('detectFraud', { resumeText: inputs.resumeText });
          break;
        default:
          showAlert('Info', 'Please select an AI Tool', 'info');
          setLoading(false);
          return;
      }
      setResult(res);
    } catch (err) {
      console.error(err);
      showAlert('Error', err.message || 'Failed to process AI task', 'error');
    } finally {
      setLoading(false);
    }
  };

  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');
  const isLoggedIn = !!token && !!userStr && userStr !== 'undefined';

  if (!isLoggedIn) {
    return null;
  }

  return (
    <>
      {/* Floating Action Trigger Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-all duration-300 flex items-center gap-2 group border border-purple-400/30"
        title="Drishti AI Assistant Suite"
      >
        <svg className="w-6 h-6 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        <span className="font-semibold text-sm max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap">
          AI Floater Hub
        </span>
      </button>

      {/* Slide-over Drawer Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-black/60 backdrop-blur-sm flex justify-end">
          <div className="w-full max-w-xl bg-white h-full shadow-2xl flex flex-col justify-between animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className="p-6 bg-gradient-to-r from-purple-700 to-indigo-800 text-white flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <span>✨</span> Drishti AI Feature Suite
                </h2>
                <p className="text-xs text-purple-200 mt-1">Instant AI powered tools for Seekers & Employers</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-purple-200 hover:text-white bg-white/10 hover:bg-white/20 p-2 rounded-full transition"
              >
                ✕
              </button>
            </div>

            {/* Mode Switcher Tabs */}
            <div className="flex border-b border-gray-200 bg-gray-50 p-2 gap-2">
              <button
                onClick={() => { setActiveTab('jobseeker'); setSelectedFeature(''); setResult(null); }}
                className={`flex-1 py-2 text-sm font-semibold rounded-lg transition ${
                  activeTab === 'jobseeker' ? 'bg-purple-600 text-white shadow' : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                👨‍🎓 Job Seeker AI
              </button>
              <button
                onClick={() => { setActiveTab('employer'); setSelectedFeature(''); setResult(null); }}
                className={`flex-1 py-2 text-sm font-semibold rounded-lg transition ${
                  activeTab === 'employer' ? 'bg-indigo-600 text-white shadow' : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                🏢 Employer AI
              </button>
            </div>

            {/* Content Body */}
            <div className="p-6 overflow-y-auto flex-grow space-y-6">
              {/* Feature Selector */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Select AI Tool:</label>
                <select
                  value={selectedFeature}
                  onChange={(e) => { setSelectedFeature(e.target.value); setResult(null); }}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                >
                  <option value="">-- Choose AI Action --</option>
                  {activeTab === 'jobseeker' ? (
                    <>
                      <option value="parseResume">📄 AI Resume Parser & Extraction</option>
                      <option value="optimizeProfile">✨ AI Profile Optimizer</option>
                      <option value="checkATS">🎯 AI Resume Score & ATS Checker</option>
                      <option value="calculateJobMatch">📊 AI Job Match Percentage</option>
                      <option value="generateCoverLetter">✉️ AI Cover Letter Generator</option>
                      <option value="analyzeSkillGap">📈 AI Skill Gap Analysis</option>
                      <option value="generateInterviewPrep">❓ AI Interview Preparation</option>
                      <option value="generateCareerRoadmap">🗺️ AI Career Roadmap</option>
                      <option value="getCareerCoachAdvice">💬 AI Career Coach</option>
                    </>
                  ) : (
                    <>
                      <option value="generateJobDescription">📝 AI Job Description Generator</option>
                      <option value="recommendSalary">💰 AI Salary Recommendation</option>
                      <option value="generateScreeningQuestions">❓ AI Screening Question Generator</option>
                      <option value="generateEmail">📧 AI Email Generator (Offer/Reject/Invite)</option>
                      <option value="detectFraud">🛡️ AI Duplicate & Fraud Detection</option>
                    </>
                  )}
                </select>
              </div>

              {/* Dynamic Inputs Form */}
              {selectedFeature && (
                <form onSubmit={handleRunAI} className="space-y-4 bg-purple-50/50 p-4 rounded-xl border border-purple-100">
                  {['parseResume', 'optimizeProfile', 'checkATS', 'generateCoverLetter', 'getCareerCoachAdvice', 'detectFraud', 'calculateJobMatch'].includes(selectedFeature) && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Resume / Profile Text:</label>
                      <textarea
                        name="resumeText"
                        rows="4"
                        placeholder="Paste resume or profile text here..."
                        value={inputs.resumeText}
                        onChange={handleInputChange}
                        className="w-full p-2.5 text-sm border rounded-lg"
                        required
                      ></textarea>
                    </div>
                  )}

                  {['checkATS', 'generateCoverLetter', 'generateScreeningQuestions', 'calculateJobMatch'].includes(selectedFeature) && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Job Description:</label>
                      <textarea
                        name="jobDescription"
                        rows="3"
                        placeholder="Paste target job description..."
                        value={inputs.jobDescription}
                        onChange={handleInputChange}
                        className="w-full p-2.5 text-sm border rounded-lg"
                        required
                      ></textarea>
                    </div>
                  )}

                  {['generateJobDescription', 'recommendSalary', 'generateEmail'].includes(selectedFeature) && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Job Title / Role:</label>
                      <input
                        type="text"
                        name="jobTitle"
                        placeholder="e.g. Senior Software Engineer"
                        value={inputs.jobTitle}
                        onChange={handleInputChange}
                        className="w-full p-2.5 text-sm border rounded-lg"
                        required
                      />
                    </div>
                  )}

                  {['generateJobDescription', 'analyzeSkillGap', 'calculateJobMatch'].includes(selectedFeature) && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Skills (comma separated):</label>
                      <input
                        type="text"
                        name="skills"
                        placeholder="React, Node.js, Python..."
                        value={inputs.skills}
                        onChange={handleInputChange}
                        className="w-full p-2.5 text-sm border rounded-lg"
                      />
                    </div>
                  )}

                  {['generateJobDescription', 'generateEmail'].includes(selectedFeature) && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Company Name:</label>
                      <input
                        type="text"
                        name="companyName"
                        placeholder="Company name"
                        value={inputs.companyName}
                        onChange={handleInputChange}
                        className="w-full p-2.5 text-sm border rounded-lg"
                      />
                    </div>
                  )}

                  {['analyzeSkillGap', 'generateInterviewPrep', 'generateCareerRoadmap'].includes(selectedFeature) && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Target Role:</label>
                      <input
                        type="text"
                        name="targetRole"
                        placeholder="e.g. Data Scientist / DevOps Lead"
                        value={inputs.targetRole}
                        onChange={handleInputChange}
                        className="w-full p-2.5 text-sm border rounded-lg"
                        required
                      />
                    </div>
                  )}

                  {['generateCareerRoadmap'].includes(selectedFeature) && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Current Role:</label>
                      <input
                        type="text"
                        name="currentRole"
                        placeholder="e.g. Junior Developer"
                        value={inputs.currentRole}
                        onChange={handleInputChange}
                        className="w-full p-2.5 text-sm border rounded-lg"
                      />
                    </div>
                  )}

                  {selectedFeature === 'getCareerCoachAdvice' && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Ask Career Coach:</label>
                      <input
                        type="text"
                        name="userQuery"
                        placeholder="How can I transition into AI engineering?"
                        value={inputs.userQuery}
                        onChange={handleInputChange}
                        className="w-full p-2.5 text-sm border rounded-lg"
                        required
                      />
                    </div>
                  )}

                  {selectedFeature === 'recommendSalary' && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Location:</label>
                        <input
                          type="text"
                          name="location"
                          placeholder="e.g. Remote / New York"
                          value={inputs.location}
                          onChange={handleInputChange}
                          className="w-full p-2.5 text-sm border rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Experience Level:</label>
                        <input
                          type="text"
                          name="experienceLevel"
                          placeholder="e.g. Mid-Level / Senior"
                          value={inputs.experienceLevel}
                          onChange={handleInputChange}
                          className="w-full p-2.5 text-sm border rounded-lg"
                        />
                      </div>
                    </div>
                  )}

                  {selectedFeature === 'generateEmail' && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Email Type:</label>
                        <select
                          name="emailType"
                          value={inputs.emailType}
                          onChange={handleInputChange}
                          className="w-full p-2.5 text-sm border rounded-lg"
                        >
                          <option value="Offer">Offer Letter</option>
                          <option value="Reject">Rejection</option>
                          <option value="Interview Invite">Interview Invite</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Candidate Name:</label>
                        <input
                          type="text"
                          name="candidateName"
                          placeholder="John Doe"
                          value={inputs.candidateName}
                          onChange={handleInputChange}
                          className="w-full p-2.5 text-sm border rounded-lg"
                        />
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold text-sm rounded-lg hover:from-purple-700 hover:to-indigo-700 shadow flex justify-center items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Generating AI Insights...
                      </>
                    ) : (
                      '🚀 Run AI Generator'
                    )}
                  </button>
                </form>
              )}

              {/* Display Result Output */}
              {result && (
                <div className="mt-4 p-4 bg-gray-900 text-green-400 rounded-xl text-xs font-mono overflow-x-auto shadow-inner border border-gray-800">
                  <div className="flex justify-between items-center mb-2 pb-1 border-b border-gray-800 text-gray-400">
                    <span className="font-sans font-semibold text-white">Result Output</span>
                    <button
                      onClick={() => navigator.clipboard.writeText(JSON.stringify(result, null, 2))}
                      className="hover:text-white font-sans text-xs bg-gray-800 px-2 py-1 rounded"
                    >
                      Copy Output
                    </button>
                  </div>
                  <pre className="whitespace-pre-wrap">{JSON.stringify(result, null, 2)}</pre>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 bg-gray-100 border-t border-gray-200 text-center text-xs text-gray-500">
              Powered by Google Gemini AI Engine • Drishti UAT
            </div>
          </div>
        </div>
      )}
    </>
  );
}
